import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { DatePicker, Empty, Select, Spin, Table } from 'antd-v5';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import AddRolexInquiryModal from '../../../../Rolex/AddRolexInquiryModal';
import { getVendorItemCollections } from '../../../../../api/vendors';
import { listInquiries } from '../../../../../api/vendors';
import { getMerchantTags } from '../../../../../services/clients';
import { transformInquiry } from '../../../../../components/Routes/Rolex/utils';

const { RangePicker } = DatePicker;

const ROLEX_VENDOR_ID = 686;

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  { label: 'Waiting', value: 'WAITING' },
  { label: 'Ordered', value: 'ORDERED' },
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Fulfilled', value: 'FULFILLED' },
];

const AllRolexInquiries = ({ auth }) => {
  const history = useHistory();
  const [collectionOptions, setCollectionOptions] = useState([]);
  const [collectionFilter, setCollectionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [tagLookup, setTagLookup] = useState([]);

  useEffect(() => {
    getVendorItemCollections([ROLEX_VENDOR_ID])
      .then(response => {
        const options = [{ label: 'All Collections', value: '' }];
        (response?.data || []).forEach(item => {
          options.push({ label: item.name, value: item.name });
        });
        setCollectionOptions(options);
      })
      .catch(() => setCollectionOptions([]));
  }, []);

  useEffect(() => {
    getMerchantTags({ query: { limit: 500 }, loadingStatusOff: true })
      .then(response => setTagLookup(response?.results || []))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const query = {
        vendorKey: 'rolex',
        associateId: auth?.userId,
        page,
        pageSize,
      };
      if (statusFilter) query.status = statusFilter;
      if (collectionFilter) query.family = collectionFilter;
      if (dateRange?.[0]) query.startDate = dateRange[0].format('YYYYMMDD');
      if (dateRange?.[1]) query.endDate = dateRange[1].format('YYYYMMDD');

      const response = await listInquiries(query);
      const stores = auth?.stores || [];
      const items = (response?.data || []).map(item => transformInquiry(item, { stores, tags: tagLookup }));
      setData(items);
      setTotal(response?.total || items.length);
    } catch {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [auth?.userId, auth?.stores, page, pageSize, statusFilter, collectionFilter, dateRange, tagLookup]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, collectionFilter, dateRange]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'clientName',
      key: 'clientName',
      sorter: (a, b) => a.clientName.localeCompare(b.clientName),
      render: (text, record) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span
          style={{ color: '#1677ff', cursor: 'pointer' }}
          onClick={() => history.push(`/Clients?client=${record.clientId}&clientTab=rolex`)}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Inquiry Date',
      dataIndex: 'inquiryDate',
      key: 'inquiryDate',
      sorter: (a, b) => (a.inquiryDateRaw || '').localeCompare(b.inquiryDateRaw || ''),
      render: (text, record) => (
        <div>
          <div style={{ fontSize: 14 }}>{text}</div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>{record.durationMonths}</div>
        </div>
      ),
    },
    {
      title: 'Last Inbound',
      dataIndex: 'lastInbound',
      key: 'lastInbound',
      render: (text, record) => (
        <div>
          <div style={{ fontSize: 14 }}>{text}</div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>{record.lastInboundDuration}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: value => {
        const label = { WAITING: 'Waiting', ORDERED: 'Ordered', CANCELLED: 'Cancelled', FULFILLED: 'Fulfilled' };
        return label[value] || value;
      },
    },
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {record.productImage && (
            <img
              src={record.productImage}
              alt={record.productMpn}
              style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }}
            />
          )}
          <div>
            <div>{text}</div>
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>
              {record.productMpn}
              {record.productCount > 1 && (
                <span style={{ color: 'rgba(0,0,0,0.35)', fontStyle: 'italic' }}>
                  {' '}
                  +{record.productCount - 1} more
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'edit',
      width: 80,
      render: (_, record) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => setEditingInquiry(record)}>
          Edit
        </span>
      ),
    },
  ];

  return (
    <StandardLayoutV2
      title="Rolex Inquiries"
      breadcrumb={[{ path: '/', title: 'Today' }, { title: 'Rolex Inquiries' }]}
      classProps="flex-col-left"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingTop: '0px' }}
    >
      <div style={{ display: 'flex', gap: 15, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <Select
          style={{ width: 170, textTransform: 'capitalize' }}
          popupClassName="all-rolex-inquiries-collection-popup"
          value={collectionFilter}
          onChange={setCollectionFilter}
          options={collectionOptions}
          placeholder="All Collections"
          showSearch
          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        />
        <Select
          style={{ width: 150 }}
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_OPTIONS}
          placeholder="All Statuses"
        />
        <RangePicker value={dateRange} onChange={setDateRange} />
      </div>

      <style>
        {'.all-rolex-inquiries-collection-popup .ant5-select-item-option-content { text-transform: capitalize; }'}
      </style>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          size="middle"
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
          }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Rolex inquiries found" /> }}
        />
      )}

      <AddRolexInquiryModal
        open={!!editingInquiry}
        onCancel={() => setEditingInquiry(null)}
        onSave={() => {
          setEditingInquiry(null);
          fetchData();
        }}
        inquiry={editingInquiry}
        vendorId={ROLEX_VENDOR_ID}
      />
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AllRolexInquiries);
